import React, { forwardRef } from 'react';
import { css } from '@emotion/react';
import { Checkbox as AntdCheckbox } from 'antd';
import type { CheckboxProps as AntdCheckboxProps } from 'antd';
import { legacyOnly, newDesignOnly } from 'constants/themes.constants';
import styled from 'types/theme.types';
import omit from 'lodash/omit';

type Env0CheckboxProps = {
  big?: true;
};

type CheckboxProps = AntdCheckboxProps & Env0CheckboxProps;

/* Legacy only: antd derives the tick geometry from checkboxSize (16), so a 20px box gets a tick sized
   for the smaller one, sitting up-left — `scale(1.25)` is the legacy compensation for exactly that.
   On the new design `big` renders the plain 16px checkbox. APO-484 deletes this. */
const BigCheckbox = styled(AntdCheckbox)`
  & span.ant-checkbox {
    ${legacyOnly(css`
      &:not(.ant-checkbox-indeterminate) {
        span.ant-checkbox-inner {
          ::after {
            transform: rotate(45deg) scale(1.25) translate(-35%, -45%);
          }
        }
      }
    `)}

    /* Literal, not theme.scale['600'] - same 20px, and several page specs render this without a
       ThemeProvider, where a nested theme read throws. */
    ${legacyOnly(css`
      span.ant-checkbox-inner {
        height: 20px;
        width: 20px;
      }
    `)}
  }
`;

/**
 * Two states the bridge cannot carry: antd draws no fill on an unchecked box's hover and takes the hover
 * border from `colorPrimary`, the checked fill; and it reuses the resting `colorBorder` when disabled.
 *
 * Both out-specify antd without `!important` — six classes against four, three against two. Re-derive
 * those counts rather than trusting them if the selectors change.
 *
 * Checked is excluded because the label text sits outside antd's wrapper: hovering it would beat antd's
 * two-class checked fill while its six-class checked-hover rule (which needs the wrapper itself hovered)
 * never fires, blanking the box. Indeterminate stays in — its resting fill is the container background.
 */
// must be `label` to bind text to checkbox input
const OuterCheckboxContainer = styled.label`
  display: flex;
  ${legacyOnly(css`
    gap: 5px;
  `)}

  ${newDesignOnly(css`
    gap: var(--spacing-xxs);
    align-items: center;

    &:hover .ant-checkbox:not(.ant-checkbox-disabled):not(.ant-checkbox-checked) .ant-checkbox-inner {
      background-color: var(--surface-primary-background);
      border-color: var(--icon-action);
    }
  `)}
`;

/**
 * `display: flex` stays between the two `legacyOnly` blocks because the parity fixture pins declaration
 * order, not just values. `legacyOnly` takes the whole props object here: these values read `big`, which a
 * theme-only signature cannot see.
 */
const CheckboxTextContainer = styled.span<Env0CheckboxProps>`
  ${props =>
    legacyOnly(css`
      font-size: ${props.big ? 14 : 13}px;
      color: ${props.theme.primaryBlack} !important;
      font-weight: 100;
    `)(props)}
  display: flex;
  ${props =>
    legacyOnly(css`
      align-items: ${props.big ? 'center' : 'end'};
    `)(props)}

  ${newDesignOnly(css`
    font-size: var(--font-size-body-md);
    line-height: var(--line-height-body-md);
    font-weight: var(--font-weight-light);
    color: var(--text-heading);
    align-items: center;
  `)}
`;

const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(({ children, big, ...props }, ref) => {
  const CheckBoxComponent = big ? BigCheckbox : AntdCheckbox;

  return (
    <OuterCheckboxContainer>
      <CheckBoxComponent ref={ref} {...omit(props, ['hideError'])} />
      <CheckboxTextContainer big={big}>{children}</CheckboxTextContainer>
    </OuterCheckboxContainer>
  );
});

Checkbox.displayName = 'Checkbox';

export default Checkbox;
